import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  assignPlotOwner,
  createPlot,
  getMyPlot,
  getPlot,
  getPlotHistory,
  listPlotsPaginated,
  releasePlotOwner,
  updatePlot,
  type CreatePlotInput,
  type ListPlotsParams,
  type UpdatePlotInput,
} from '@/lib/plots'

export function usePlotsList(params: ListPlotsParams) {
  return useQuery({
    queryKey: ['plots', 'list', params],
    queryFn: () => listPlotsPaginated(params),
  })
}

export function usePlot(id: string | undefined) {
  return useQuery({
    queryKey: ['plots', id],
    queryFn: () => getPlot(id!),
    enabled: !!id,
  })
}

export function useMyPlot(plotId: string | undefined) {
  return useQuery({
    queryKey: ['plots', 'my', plotId],
    queryFn: () => getMyPlot(plotId!),
    enabled: !!plotId,
  })
}

export function usePlotHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['plots', id, 'history'],
    queryFn: () => getPlotHistory(id!),
    enabled: !!id,
  })
}

export function useCreatePlot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePlotInput) => createPlot(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
    },
  })
}

export function useUpdatePlot(plotId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdatePlotInput) => updatePlot(plotId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
    },
  })
}

interface AssignInput {
  memberId: string
  transferNote?: string
  overrideDuesReason?: string
}

interface ReleaseInput {
  transferNote?: string
  overrideDuesReason?: string
}

export function useAssignPlotOwner(plotId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, transferNote, overrideDuesReason }: AssignInput) =>
      assignPlotOwner(plotId, memberId, transferNote, overrideDuesReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useReleasePlotOwner(plotId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ReleaseInput = {}) =>
      releasePlotOwner(plotId, input.transferNote, input.overrideDuesReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useAssignPlotToMember(memberId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ plotId, transferNote, overrideDuesReason }: ReleaseInput & { plotId: string }) =>
      assignPlotOwner(plotId, memberId, transferNote, overrideDuesReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useReleasePlotById() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ plotId, transferNote, overrideDuesReason }: ReleaseInput & { plotId: string }) =>
      releasePlotOwner(plotId, transferNote, overrideDuesReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
  })
}
